import { supabase } from './supabase.js'
import { peso, shortDate, todayISO } from './format.js'

/**
 * The notes side: categories, captured entries, projects, tasks, reminders,
 * the log, and links between any two things.
 *
 * Same shape as Alex's app. The one difference is the first category:
 * Cashflow sits where 4ocean sat, and opens the money screens instead of a
 * list of notes.
 */

/* The five he named. Only ever written on his tap, never automatically. */
export const STARTER_CATEGORIES = [
  { name: 'Cashflow',    color: '#12A150' },
  { name: 'Health',      color: '#FF2D55' },
  { name: 'Personal',    color: '#DD6B00' },
  { name: 'Notes',       color: '#5856D6' },
  { name: 'My Projects', color: '#AF52DE' },
]

/**
 * Two categories carry an extra screen. Both are matched through their alias
 * list as well as their current name, so renaming either one keeps working.
 */
function named(cat, name) {
  if (!cat) return false
  if (cat.name === name) return true
  return (cat.aliases || []).includes(name)
}

export const CASHFLOW = 'Cashflow'
export const HEALTH = 'Health'
export const isCashflow = (cat) => named(cat, CASHFLOW)
export const isHealth = (cat) => named(cat, HEALTH)

async function logIt(kind, summary, targetType, targetId) {
  try {
    await supabase.from('activity_log').insert({
      kind, summary, target_type: targetType ?? null, target_id: targetId ?? null,
    })
  } catch {
    /* never let the log fail the real write */
  }
}

/* ── Categories ─────────────────────────────────────────────── */

export async function getCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .is('parent_id', null)
    .order('sort_order')
  if (error) throw error
  return data ?? []
}

/** One tap on the empty state. Never runs on its own. */
export async function createStarterCategories() {
  const rows = STARTER_CATEGORIES.map((c, i) => ({ ...c, sort_order: i }))
  const { data, error } = await supabase.from('categories').insert(rows).select()
  if (error) throw error
  await logIt('category.seed', 'Categories set up')
  return data ?? []
}

export async function renameCategory(cat, name) {
  /* Old names become aliases so search and links keep working. Nothing
     anywhere stores a category name — only its id. */
  const aliases = Array.from(new Set([...(cat.aliases || []), cat.name]))
  const { error } = await supabase
    .from('categories')
    .update({ name, aliases })
    .eq('id', cat.id)
  if (error) throw error
  await logIt('category.rename', cat.name + ' renamed to ' + name, 'category', cat.id)
}

/* ── Capture ────────────────────────────────────────────────── */

/**
 * Capture is typed, and files nothing. Category comes later, on his terms.
 * An entry with no category_id is what the Inbox shows.
 */
export async function capture(body, categoryId) {
  const { data, error } = await supabase
    .from('entries')
    .insert({
      body: body.trim(),
      category_id: categoryId ?? null,
      filed_at: categoryId ? new Date().toISOString() : null,
    })
    .select()
    .single()
  if (error) throw error
  await logIt('entry.capture', body.trim().slice(0, 80), 'entry', data.id)
  return data
}

export async function fileEntry(id, categoryId) {
  const { error } = await supabase
    .from('entries')
    .update({ category_id: categoryId, filed_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
  await logIt('entry.filed', 'Filed', 'entry', id)
}

/* Rewording a note in place. The Log keeps the edit as its own line, so the
   original capture is never lost even though the note now reads differently. */
export async function updateEntry(id, body) {
  const text = body.trim()
  const { error } = await supabase
    .from('entries')
    .update({ body: text })
    .eq('id', id)
  if (error) throw error
  await logIt('entry.edit', text.slice(0, 80), 'entry', id)
}

export async function getInbox() {
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .is('category_id', null)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data ?? []
}

export async function getEntries(categoryId) {
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .eq('category_id', categoryId)
    .is('topic_id', null)
    .order('created_at', { ascending: false })
    .limit(200)
  if (error) throw error
  return data ?? []
}

/* ── Notes ──────────────────────────────────────────────────
 * A note is a page he keeps adding to for months or years: "My thoughts on
 * XYZ". The schema had this from the first batch and nothing used it. The
 * page is a topic, and every addition is an ordinary entry carrying the
 * topic's id, so Search and the Log already see them without new plumbing.
 * Entries with no topic are quick captures from the pencil and stay apart.
 */

/* Notes and projects live on a category screen, so opening one from a link
   elsewhere starts with finding which category that is. */
export async function categoryOf(type, id) {
  const table = type === 'topic' ? 'topics' : type === 'project' ? 'projects' : null
  if (!table) return null
  const { data, error } = await supabase.from(table).select('category_id').eq('id', id).maybeSingle()
  if (error) throw error
  return data?.category_id ?? null
}

export async function getTopics(categoryId) {
  const { data: topics, error } = await supabase
    .from('topics')
    .select('*')
    .eq('category_id', categoryId)
    .order('created_at', { ascending: false })
  if (error) throw error
  if (!topics?.length) return []

  const { data: rows, error: e2 } = await supabase
    .from('entries')
    .select('topic_id, created_at')
    .in('topic_id', topics.map((t) => t.id))
  if (e2) throw e2

  const stats = new Map()
  for (const r of rows ?? []) {
    const s = stats.get(r.topic_id) ?? { count: 0, last: null }
    s.count += 1
    if (!s.last || r.created_at > s.last) s.last = r.created_at
    stats.set(r.topic_id, s)
  }
  /* Most recently written in first: the note he is living in sits on top. */
  return topics
    .map((t) => ({
      ...t,
      entry_count: stats.get(t.id)?.count ?? 0,
      last_added: stats.get(t.id)?.last ?? t.created_at,
    }))
    .sort((a, b) => (a.last_added < b.last_added ? 1 : -1))
}

export async function addTopic({ title, categoryId }) {
  const { data, error } = await supabase
    .from('topics')
    .insert({ title: title.trim(), category_id: categoryId })
    .select()
    .single()
  if (error) throw error
  await logIt('topic.create', title.trim().slice(0, 80), 'topic', data.id)
  return data
}

export async function renameTopic(id, title) {
  const { error } = await supabase
    .from('topics')
    .update({ title: title.trim() })
    .eq('id', id)
  if (error) throw error
  await logIt('topic.rename', title.trim().slice(0, 80), 'topic', id)
}

/* Oldest first. The page reads top to bottom as the thinking happened. */
export async function getTopicEntries(topicId) {
  const { data, error } = await supabase
    .from('entries')
    .select('*')
    .eq('topic_id', topicId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data ?? []
}

export async function addTopicEntry(topic, body) {
  const text = body.trim()
  const { data, error } = await supabase
    .from('entries')
    .insert({
      body: text,
      topic_id: topic.id,
      category_id: topic.category_id,
      filed_at: new Date().toISOString(),
    })
    .select()
    .single()
  if (error) throw error
  await logIt('entry.add', topic.title.slice(0, 40) + ': ' + text.slice(0, 60), 'entry', data.id)
  return data
}

/* A whole note goes, entries and links with it. Order matters: the entries'
   foreign key is ON DELETE SET NULL, so deleting the note first would not
   remove its entries but tip them all into Quick captures. Links are loose
   references with no key, so they are cleared by hand rather than left
   reading "No longer there" on everything the note was linked to. */
export async function deleteTopic(topic) {
  const { error: e1 } = await supabase.from('entries').delete().eq('topic_id', topic.id)
  if (e1) throw e1
  const { error: e2 } = await supabase
    .from('links')
    .delete()
    .or(
      'and(from_type.eq.topic,from_id.eq.' + topic.id + '),' +
      'and(to_type.eq.topic,to_id.eq.' + topic.id + ')',
    )
  if (e2) throw e2
  const { error: e3 } = await supabase.from('topics').delete().eq('id', topic.id)
  if (e3) throw e3
  await logIt('topic.delete', topic.title.slice(0, 80), 'topic', topic.id)
}

/* Gone from the note, but the Log line keeps what it said. */
export async function deleteEntry(entry) {
  const { error } = await supabase.from('entries').delete().eq('id', entry.id)
  if (error) throw error
  await logIt('entry.delete', entry.body.slice(0, 80), 'entry', entry.id)
}

/* ── Today ──────────────────────────────────────────────────── */

/**
 * Everything due today or before, still open, plus today's reminders and
 * whatever is sitting unfiled. Overdue is the point: an item scheduled last
 * week for today has to appear today.
 */
export async function getToday() {
  const today = todayISO()
  const endOfDay = today + 'T23:59:59'

  const [tasks, reminders, inbox] = await Promise.all([
    supabase
      .from('tasks')
      .select('*')
      .eq('status', 'Open')
      .lte('due_on', today)
      .order('due_on'),
    /* done_at, not sent_at. sent_at means the push went out, which is the
       moment he most wants the reminder in front of him, not the moment it
       should disappear. */
    supabase
      .from('reminders')
      .select('*')
      .is('done_at', null)
      .lte('remind_at', endOfDay)
      .order('remind_at'),
    supabase.from('entries').select('id').is('category_id', null),
  ])
  if (tasks.error) throw tasks.error
  if (reminders.error) throw reminders.error

  return {
    tasks: tasks.data ?? [],
    reminders: reminders.data ?? [],
    inboxCount: (inbox.data ?? []).length,
    today,
  }
}

export async function setTaskDone(task, done) {
  const { error } = await supabase
    .from('tasks')
    .update({
      status: done ? 'Done' : 'Open',
      done_at: done ? new Date().toISOString() : null,
    })
    .eq('id', task.id)
  if (error) throw error
  await logIt(done ? 'task.done' : 'task.reopen', task.body.slice(0, 80), 'task', task.id)
}

export async function addTask({ body, categoryId, dueOn }) {
  const { data, error } = await supabase
    .from('tasks')
    .insert({ body: body.trim(), category_id: categoryId ?? null, due_on: dueOn ?? null })
    .select()
    .single()
  if (error) throw error
  await logIt('task.add', body.trim().slice(0, 80), 'task', data.id)
  return data
}

/* ── Board ──────────────────────────────────────────────────── */

/** A project title with a plain column of dash items. No kanban, nothing drags. */
export async function getBoard(categoryId) {
  let q = supabase.from('projects').select('*').is('closed_at', null).order('sort_order')
  if (categoryId) q = q.eq('category_id', categoryId)
  const { data: projects, error } = await q
  if (error) throw error

  const ids = (projects ?? []).map((p) => p.id)
  let items = []
  if (ids.length) {
    const { data, error: e2 } = await supabase
      .from('project_items')
      .select('*')
      .in('project_id', ids)
      .order('sort_order')
    if (e2) throw e2
    items = data ?? []
  }
  return { projects: projects ?? [], items }
}

export async function addProject({ title, categoryId }) {
  const { data, error } = await supabase
    .from('projects')
    .insert({ title: title.trim(), category_id: categoryId ?? null })
    .select()
    .single()
  if (error) throw error
  await logIt('project.add', title.trim(), 'project', data.id)
  return data
}

export async function addProjectItem({ projectId, body, sortOrder }) {
  const { data, error } = await supabase
    .from('project_items')
    .insert({ project_id: projectId, body: body.trim(), sort_order: sortOrder ?? 999 })
    .select()
    .single()
  if (error) throw error
  return data
}

/** Struck through in place, then it folds away. Nothing is ever deleted. */
export async function setItemDone(item, done) {
  const { error } = await supabase
    .from('project_items')
    .update({
      status: done ? 'Done' : 'Open',
      done_at: done ? new Date().toISOString() : null,
    })
    .eq('id', item.id)
  if (error) throw error
  await logIt(done ? 'item.done' : 'item.reopen', item.body.slice(0, 80), 'project_item', item.id)
}

/* ── Reminders ──────────────────────────────────────────────── */

export async function getReminders() {
  const { data, error } = await supabase
    .from('reminders')
    .select('*')
    .is('done_at', null)
    .order('remind_at')
  if (error) throw error
  return data ?? []
}

export async function addReminder({ body, remindAt, targetType, targetId }) {
  const { data, error } = await supabase
    .from('reminders')
    .insert({
      body: body.trim(),
      remind_at: remindAt,
      target_type: targetType ?? null,
      target_id: targetId ?? null,
    })
    .select()
    .single()
  if (error) throw error
  await logIt('reminder.add', body.trim().slice(0, 80), 'reminder', data.id)
  return data
}

/**
 * Finished with. Deliberately separate from sent_at, which belongs to the
 * Worker: one says the notification went out, the other says he is done with
 * it, and conflating them is what made a reminder disappear the instant it
 * fired.
 */
export async function dismissReminder(id) {
  const { error } = await supabase
    .from('reminders')
    .update({ done_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

/* ── Links ──────────────────────────────────────────────────── */

/**
 * Anything can point at anything. A payable at a project, a reminder at a
 * board item, a build at the note where he thought of it.
 *
 * A link never moves money and never changes state. It is a cross-reference,
 * which is what makes it safe to link freely.
 */
export async function getLinks(type, id) {
  const { data, error } = await supabase
    .from('links')
    .select('*')
    .or(
      'and(from_type.eq.' + type + ',from_id.eq.' + id + '),' +
      'and(to_type.eq.' + type + ',to_id.eq.' + id + ')',
    )
  if (error) throw error
  /* Present every link from the caller's side, so the UI never has to care
     which end it was created from. */
  return (data ?? []).map((l) =>
    l.from_type === type && l.from_id === id
      ? { id: l.id, type: l.to_type, targetId: l.to_id, note: l.note }
      : { id: l.id, type: l.from_type, targetId: l.from_id, note: l.note },
  )
}

export async function addLink(from, to, note) {
  const { data, error } = await supabase
    .from('links')
    .insert({
      from_type: from.type, from_id: from.id,
      to_type: to.type, to_id: to.id,
      note: note ?? null,
    })
    .select()
    .single()
  if (error) throw error
  await logIt('link.add', from.type + ' linked to ' + to.type, from.type, from.id)
  return data
}

/*
 * What each link kind is called and where its label lives. Nothing stores a
 * label — they are resolved on read, so renaming a project renames it
 * everywhere it is linked, with nothing to migrate.
 */
/*
 * `extra` are columns fetched alongside the name, and `detail` turns them into
 * the line under it. A link that only ever said "Vietnam" made him open the
 * other screen to learn anything — the number is the reason he linked it.
 */
export const LINK_KINDS = [
  { type: 'project',      label: 'Project',     table: 'projects',           field: 'title',
    extra: 'closed_at',
    detail: (r) => (r.closed_at ? 'Closed' : null) },
  { type: 'project_item', label: 'Board item',  table: 'project_items',      field: 'body',
    extra: 'status',
    detail: (r) => (r.status === 'Done' ? 'Done' : null) },
  { type: 'task',         label: 'Task',        table: 'tasks',              field: 'body',
    extra: 'status, due_on',
    detail: (r) => (r.status === 'Done' ? 'Done' : r.due_on ? 'Due ' + shortDate(r.due_on) : null) },
  { type: 'reminder',     label: 'Reminder',    table: 'reminders',          field: 'body',
    extra: 'remind_at, sent_at',
    detail: (r) => (r.sent_at ? 'Sent' : shortDate(r.remind_at.slice(0, 10))) },
  /* A note is a page now, so the page is what links. A one-line pencil
     capture is still linkable, under its own name, and the picker leaves out
     entries that belong to a note so the same line is not offered twice. */
  { type: 'topic',        label: 'Note',        table: 'topics',             field: 'title' },
  { type: 'entry',        label: 'Capture',     table: 'entries',            field: 'body',
    whereNull: 'topic_id' },
  { type: 'build',        label: 'Build',       table: 'builds',             field: 'name',
    extra: 'target_amount, status',
    /* Raised is a sum of payments, not a column, so it arrives separately. */
    detail: (r, agg) => {
      const raised = agg?.build?.[r.id] ?? 0
      const t = Number(r.target_amount) || 0
      return peso(raised) + ' of ' + peso(t)
        + (r.status === 'Completed' ? ' · done' : '')
    },
    money: (r, agg) => ({ amount: agg?.build?.[r.id] ?? 0, settled: true }) },
  { type: 'payable',      label: 'Payable',     table: 'payables',           field: 'name',
    extra: 'amount, status',
    detail: (r) => peso(r.amount) + ' · ' + (r.status === 'Paid' ? 'paid' : 'unpaid'),
    /* What this link is worth, as a number rather than a sentence, so a list
       of them can be added up. Money already spent and money still owed are
       kept apart: adding them would claim he has put in more than he has. */
    money: (r) => ({ amount: Number(r.amount) || 0, settled: r.status === 'Paid' }) },
  { type: 'installment',  label: 'Plan',        table: 'installment_plans',  field: 'name',
    extra: 'term_count, total_amount',
    detail: (r, agg) => (agg?.installment?.[r.id] ?? 0) + ' of ' + r.term_count },
  /* Subscriptions share a table with the plain monthly defaults, so this one
     kind narrows to the rows that are actually subscriptions. */
  { type: 'subscription', label: 'Subscription', table: 'payable_defaults',  field: 'name',
    where: ['is_subscription', true],
    extra: 'amount, every_n_months',
    detail: (r) => peso(r.amount)
      + (Number(r.every_n_months) > 1 ? ' every ' + r.every_n_months + ' months' : ' a month') },
  { type: 'account',      label: 'Account',     table: 'accounts',           field: 'name',
    extra: 'balance',
    detail: (r) => peso(r.balance) },
  /*
   * A single movement in or out of an account.
   *
   * Never offered in the link picker — nobody opens a project and goes
   * hunting for a withdrawal. The link is made the other way round, from the
   * Deposit/Withdraw sheet, where he already knows what the money is for.
   * This entry exists so the link renders and counts once it is there.
   */
  /* Income that was deposited straight into an account. Made from the income
     form, never from the picker, so it is not offered there. */
  { type: 'income_entry', label: 'Income',      table: 'income_entries',     field: 'note',
    pickable: false,
    extra: 'amount, received_on',
    detail: (r) => peso(r.amount) + ' · ' + shortDate(String(r.received_on).slice(0, 10)) },
  { type: 'account_move', label: 'Money out',   table: 'account_adjustments', field: 'reason',
    pickable: false,
    extra: 'kind, old_balance, new_balance, changed_on',
    detail: (r) => {
      const moved = Number(r.old_balance || 0) - Number(r.new_balance || 0)
      return peso(Math.abs(moved)) + ' · ' + shortDate(String(r.changed_on).slice(0, 10))
    },
    /* Only a withdrawal counts toward what a thing has cost. A deposit is
       saving toward something, which is what a Build is for, and paying down
       a card funds nothing. */
    money: (r) => {
      if (r.kind !== 'withdraw') return null
      return { amount: Number(r.old_balance || 0) - Number(r.new_balance || 0), settled: true }
    } },
]

const KIND = Object.fromEntries(LINK_KINDS.map((k) => [k.type, k]))

/** Everything of one kind he could link to, newest first. */
export async function linkCandidates(type) {
  const k = KIND[type]
  if (!k) return []
  let q = supabase.from(k.table).select('id, ' + k.field)
  if (k.where) q = q.eq(k.where[0], k.where[1])
  if (k.whereNull) q = q.is(k.whereNull, null)
  const { data, error } = await q.order('created_at', { ascending: false }).limit(80)
  if (error) throw error
  return (data ?? []).map((r) => ({ id: r.id, label: r[k.field] }))
}

/** Labels for links already made, one query per kind rather than per link. */
/** Money raised per build, for the linked builds only. */
async function buildTotals(ids) {
  const { data } = await supabase
    .from('build_payments').select('build_id, amount').in('build_id', ids)
  const out = {}
  for (const r of data ?? []) out[r.build_id] = (out[r.build_id] || 0) + Number(r.amount || 0)
  return out
}

/** Charges ticked paid per plan, for the linked plans only. */
async function planPaidCounts(ids) {
  const { data } = await supabase
    .from('installment_charges').select('plan_id, status').in('plan_id', ids)
  const out = {}
  for (const r of data ?? []) {
    if (r.status === 'Paid') out[r.plan_id] = (out[r.plan_id] || 0) + 1
  }
  return out
}

export async function resolveLinks(links) {
  const byType = {}
  for (const l of links) (byType[l.type] ||= []).push(l.targetId)

  const rows = {}
  const agg = {}

  await Promise.all(
    Object.entries(byType).map(async ([type, ids]) => {
      const k = KIND[type]
      if (!k) return
      const cols = ['id', k.field, k.extra].filter(Boolean).join(', ')
      const { data } = await supabase.from(k.table).select(cols).in('id', ids)
      for (const r of data ?? []) rows[type + ':' + r.id] = r

      /* Two kinds carry a figure that is a sum rather than a column. Only
         fetched when a link of that kind is actually present. */
      if (type === 'build') agg.build = await buildTotals(ids)
      if (type === 'installment') agg.installment = await planPaidCounts(ids)
    }),
  )

  return links.map((l) => {
    const k = KIND[l.type]
    const row = rows[l.type + ':' + l.targetId]
    let detail = null
    if (row && k?.detail) {
      /* A detail that throws must not take the whole list down with it. */
      try { detail = k.detail(row, agg) } catch { detail = null }
    }
    let money = null
    if (row && k?.money) {
      try { money = k.money(row, agg) } catch { money = null }
    }

    return {
      ...l,
      kindLabel: k?.label ?? l.type,
      /* A target deleted out from under a link says so rather than showing a
         blank row. */
      /* Some kinds are named by an optional column - a movement's reason -
         so fall back to the kind rather than rendering an empty row. */
      label: row ? (row[k.field] || k.label) : 'No longer there',
      detail,
      money,
    }
  })
}

export async function removeLink(id) {
  const { error } = await supabase.from('links').delete().eq('id', id)
  if (error) throw error
}

/* ── The Log and Search ─────────────────────────────────────── */

const LOG_FILTERS = {
  all: null,
  said: ['entry.capture'],
  made: ['project.add', 'task.add', 'build.add', 'payout.add', 'reminder.add', 'link.add'],
  done: ['item.done', 'task.done', 'payable.paid', 'build.payment'],
}

export async function getLog(filter = 'all', limit = 200) {
  let q = supabase
    .from('activity_log')
    .select('*')
    .order('happened_at', { ascending: false })
    .limit(limit)
  const kinds = LOG_FILTERS[filter]
  if (kinds) q = q.in('kind', kinds)
  const { data, error } = await q
  if (error) throw error
  return data ?? []
}

export async function search(q) {
  const term = q.trim()
  if (term.length < 2) return { entries: [], tasks: [], projects: [] }
  const like = '%' + term + '%'

  const [entries, tasks, projects] = await Promise.all([
    supabase.from('entries').select('*').ilike('body', like).limit(40),
    supabase.from('tasks').select('*').ilike('body', like).limit(40),
    supabase.from('projects').select('*').ilike('title', like).limit(40),
  ])
  if (entries.error) throw entries.error
  return {
    entries: entries.data ?? [],
    tasks: tasks.data ?? [],
    projects: projects.data ?? [],
  }
}
